// crypto.js — Toute la cryptographie a lieu ICI, dans le navigateur.
// Le serveur ne voit jamais autre chose que des blobs chiffrés.
//
// Choix figés (cf. cahier des charges) :
//   - libsodium.js (libsodium-wrappers), aucune réimplémentation maison
//   - Échange de clés : X25519 (ECDH), intégré à crypto_box
//   - Chiffrement : XSalsa20-Poly1305 via crypto_box (authentifié)
//   - Pas de Double Ratchet en v1 → pas de forward secrecy parfait (cf. SECURITY.md)
// v3.1 : paquet SUMO — le paquet standard 'libsodium-wrappers' casse le
// build Vite (« Could not resolve ./libsodium.mjs ») ; le sumo embarque
// toutes les primitives (dont crypto_pwhash/Argon2id, indispensable ici)
// avec une résolution ESM correcte. NE PAS revenir au paquet standard.
import _sodium from 'libsodium-wrappers-sumo';
import { WORDS } from './wordlist.js';

let sodium = null;
export async function initSodium() {
  await _sodium.ready;
  sodium = _sodium;
  return sodium;
}

// v4.9.0 : accès contrôlé aux primitives libsodium déjà initialisées pour le
// coffre local. Aucun secret global n'est exposé : seul l'objet bibliothèque
// est retourné, la clé maître du coffre reste dans local-vault.js.
export function requireSodium() {
  if (!sodium) throw new Error('Cryptographie locale non initialisée.');
  return sodium;
}

const B64 = () => sodium.base64_variants.ORIGINAL;
export const toB64 = (u8) => sodium.to_base64(u8, B64());
export const fromB64 = (s) => sodium.from_base64(s, B64());

// --- Génération de la paire de clés X25519 (à l'inscription) ---
// La clé privée ne quitte JAMAIS ce navigateur (stockée en IndexedDB).
export function generateKeyPair() {
  const kp = sodium.crypto_box_keypair();
  return { publicKey: toB64(kp.publicKey), privateKey: toB64(kp.privateKey) };
}

// --- Empreinte (fingerprint) de clé publique, type Signal ---
// Hash court comparable manuellement par les deux interlocuteurs.
export function fingerprint(publicKeyB64) {
  const h = sodium.crypto_generichash(16, fromB64(publicKeyB64));
  const hex = sodium.to_hex(h).toUpperCase();
  return hex.match(/.{4}/g).join(' '); // groupes de 4 pour la lisibilité
}

// --- Chiffrement d'un message (texte ou binaire) pour un destinataire ---
// crypto_box = ECDH X25519 (clé de session dérivée par paire) + XSalsa20-Poly1305.
// Un nonce aléatoire unique par message.
export function encryptFor(data, theirPublicKeyB64, myPrivateKeyB64) {
  const bytes = typeof data === 'string' ? sodium.from_string(data) : new Uint8Array(data);
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const ct = sodium.crypto_box_easy(bytes, nonce, fromB64(theirPublicKeyB64), fromB64(myPrivateKeyB64));
  return { ciphertext: toB64(ct), nonce: toB64(nonce) };
}

// --- Déchiffrement (échoue si le message a été altéré : Poly1305) ---
export function decryptFrom(ciphertextB64, nonceB64, theirPublicKeyB64, myPrivateKeyB64, asBytes = false) {
  const pt = sodium.crypto_box_open_easy(
    fromB64(ciphertextB64), fromB64(nonceB64),
    fromB64(theirPublicKeyB64), fromB64(myPrivateKeyB64)
  );
  return asBytes ? pt : sodium.to_string(pt);
}

// --- Réponse au challenge d'authentification du serveur ---
// Le serveur envoie un nonce scellé (crypto_box_seal) avec notre clé publique ;
// seule notre clé privée peut l'ouvrir → preuve de possession.
export function openChallenge(boxB64, myPublicKeyB64, myPrivateKeyB64) {
  const nonce = sodium.crypto_box_seal_open(fromB64(boxB64), fromB64(myPublicKeyB64), fromB64(myPrivateKeyB64));
  return toB64(nonce);
}

// --- Validation du type réel de fichier par magic bytes, CÔTÉ CLIENT ---
// Note d'architecture : le serveur ne peut pas inspecter le type MIME réel
// puisqu'il ne reçoit qu'un blob chiffré (c'est le principe même du E2E).
// La vérification des octets magiques se fait donc ici, avant chiffrement.
// Le serveur, lui, valide la taille (20 Mo) et ne stocke aucune extension.
const MAGIC = [
  { type: 'image',    mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { type: 'image',    mime: 'image/png',  bytes: [0x89, 0x50, 0x4e, 0x47] },
  { type: 'image',    mime: 'image/gif',  bytes: [0x47, 0x49, 0x46, 0x38] },
  { type: 'image',    mime: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46], offset8: [0x57, 0x45, 0x42, 0x50] },
  { type: 'document', mime: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46] },
  // Vidéos (v2.0) : MP4/MOV = 'ftyp' à l'offset 4 ; WebM = en-tête EBML.
  { type: 'video',    mime: 'video/mp4',  offset4: [0x66, 0x74, 0x79, 0x70], bytes: [] },
  { type: 'video',    mime: 'video/webm', bytes: [0x1a, 0x45, 0xdf, 0xa3] },
  // DOCX / XLSX sont des ZIP (PK) — départagés ensuite par l'extension.
  { type: 'archive',  mime: 'application/zip', bytes: [0x50, 0x4b, 0x03, 0x04] },
  { type: 'archive',  mime: 'application/x-rar', bytes: [0x52, 0x61, 0x72, 0x21] },
  { type: 'archive',  mime: 'application/x-7z-compressed', bytes: [0x37, 0x7a, 0xbc, 0xaf] },
  // v4.1 : audio (fichier existant partagé depuis le sélecteur — l'audio
  // ENREGISTRÉ via le micro de l'app ne passe pas par cette détection, voir
  // App.jsx `__forceType`). MP3 (ID3), OGG, WAV, M4A (conteneur 'ftyp' partagé
  // avec la vidéo MP4, départagé par l'extension ci-dessous comme DOCX/ZIP).
  { type: 'audio', mime: 'audio/mpeg', bytes: [0x49, 0x44, 0x33] },
  { type: 'audio', mime: 'audio/ogg',  bytes: [0x4f, 0x67, 0x67, 0x53] },
  { type: 'audio', mime: 'audio/wav',  bytes: [0x52, 0x49, 0x46, 0x46], offset8: [0x57, 0x41, 0x56, 0x45] },
];
const EXT_OK = { jpg: 'image', jpeg: 'image', png: 'image', webp: 'image', gif: 'image',
  mp4: 'video', webm: 'video', mov: 'video',
  pdf: 'document', docx: 'document', xlsx: 'document', txt: 'document',
  zip: 'archive', rar: 'archive', '7z': 'archive',
  mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio', oga: 'audio' };

export function classifyFile(name, bytes) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const extType = EXT_OK[ext];
  if (!extType) return { ok: false, error: 'Type de fichier non accepté.' };

  // TXT : pas de magic bytes fiables — extension suffisante (contenu chiffré de toute façon).
  if (ext === 'txt') return { ok: true, msgType: 'document' };

  const head = new Uint8Array(bytes.slice(0, 12));
  const m = MAGIC.find((s) =>
    s.bytes.every((b, i) => head[i] === b) &&
    (!s.offset4 || s.offset4.every((b, i) => head[4 + i] === b)) &&
    (!s.offset8 || s.offset8.every((b, i) => head[8 + i] === b))
  );
  if (!m) return { ok: false, error: 'Le contenu du fichier ne correspond pas à son extension.' };

  // DOCX/XLSX : signature ZIP + extension bureautique → document.
  // M4A : signature 'ftyp' (identique au conteneur vidéo MP4) + extension
  // audio → audio, même logique de désambiguïsation par extension.
  const msgType = (m.mime === 'application/zip' && (ext === 'docx' || ext === 'xlsx')) ? 'document'
    : (extType === 'audio' && (m.type === 'video' || m.type === 'audio')) ? 'audio'
    : m.type;
  if (msgType !== extType) return { ok: false, error: 'Le contenu du fichier ne correspond pas à son extension.' };
  return { ok: true, msgType };
}

// --- Export/import d'identité chiffrée par passphrase (migration d'appareil) ---
// Dérivation de clé : Argon2id (crypto_pwhash, paramètres MODERATE) + sel aléatoire.
// Chiffrement : crypto_secretbox (XSalsa20-Poly1305). Le fichier exporté peut
// transiter par n'importe quel canal : sans la passphrase il est inutilisable.
// La clé privée ne circule donc jamais en clair, même pendant une migration.
export function exportIdentity(identity, passphrase) {
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const key = sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES, passphrase, salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE, sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const plaintext = sodium.from_string(JSON.stringify(identity));
  const ct = sodium.crypto_secretbox_easy(plaintext, nonce, key);
  sodium.memzero(key);
  return JSON.stringify({
    v: 1, kdf: 'argon2id13-moderate',
    salt: toB64(salt), nonce: toB64(nonce), box: toB64(ct),
  });
}

export function importIdentity(fileText, passphrase) {
  const data = JSON.parse(fileText);
  if (data.v !== 1) throw new Error('Format de fichier inconnu.');
  const key = sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES, passphrase, fromB64(data.salt),
    sodium.crypto_pwhash_OPSLIMIT_MODERATE, sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
  let pt;
  try {
    pt = sodium.crypto_secretbox_open_easy(fromB64(data.box), fromB64(data.nonce), key);
  } catch (_) {
    throw new Error('Passphrase incorrecte ou fichier corrompu.');
  } finally {
    sodium.memzero(key);
  }
  const identity = JSON.parse(sodium.to_string(pt));
  if (!identity.pseudo || !identity.publicKey || !identity.privateKey) throw new Error('Fichier invalide.');
  return identity;
}

// --- v1.2 (correctif pièces jointes) : chiffrement binaire sans base64 ---
// L'ancien pipeline convertissait le ciphertext en base64 puis le reconvertissait
// en octets avant upload (~4 copies mémoire d'un fichier de 20 Mo sur le thread
// principal). Safari, surtout iOS, tue les pages sous pression mémoire — ces
// fonctions travaillent en Uint8Array de bout en bout (une seule copie).
export function encryptBytesFor(bytes, theirPublicKeyB64, myPrivateKeyB64) {
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const ct = sodium.crypto_box_easy(new Uint8Array(bytes), nonce,
    fromB64(theirPublicKeyB64), fromB64(myPrivateKeyB64));
  return { ciphertext: ct, nonce: toB64(nonce) }; // ciphertext = Uint8Array brut
}

export function decryptBytesFrom(ciphertextBytes, nonceB64, theirPublicKeyB64, myPrivateKeyB64) {
  return sodium.crypto_box_open_easy(new Uint8Array(ciphertextBytes), fromB64(nonceB64),
    fromB64(theirPublicKeyB64), fromB64(myPrivateKeyB64)); // → Uint8Array
}

// --- v2.0 : vidéos (magic bytes) + chiffrement par morceaux (chunked) ---
// Un fichier > 20 Mo (vidéos) est découpé en morceaux de 5 Mo, chacun chiffré
// indépendamment avec son PROPRE nonce (aucune réutilisation), puis encadré :
//   trame = [taille u32 big-endian][nonce 24 o][ciphertext]
// Avantages : progression précise, reprise moins coûteuse, pas de tampon
// mémoire géant d'un seul tenant côté chiffrement.
export const CHUNK_SIZE = 5 * 1024 * 1024;
export const VIDEO_MAX = 100 * 1024 * 1024;
export const FILE_MAX = 20 * 1024 * 1024;

export function encryptChunk(bytes, theirPublicKeyB64, myPrivateKeyB64) {
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const ct = sodium.crypto_box_easy(new Uint8Array(bytes), nonce,
    fromB64(theirPublicKeyB64), fromB64(myPrivateKeyB64));
  const frame = new Uint8Array(4 + nonce.length + ct.length);
  new DataView(frame.buffer).setUint32(0, nonce.length + ct.length);
  frame.set(nonce, 4);
  frame.set(ct, 4 + nonce.length);
  return frame;
}

// Déchiffre un fichier encadré complet → tableau de Uint8Array (assemblé en
// Blob([parts]) par l'appelant : évite un unique buffer de 100 Mo).
export function decryptChunkedFile(buf, theirPublicKeyB64, myPrivateKeyB64) {
  const bytes = new Uint8Array(buf);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const NPUB = sodium.crypto_box_NONCEBYTES;
  const parts = [];
  let off = 0;
  while (off < bytes.length) {
    const len = view.getUint32(off); off += 4;
    if (len < NPUB + 16 || off + len > bytes.length) throw new Error('Fichier corrompu.');
    const nonce = bytes.subarray(off, off + NPUB);
    const ct = bytes.subarray(off + NPUB, off + len);
    parts.push(sodium.crypto_box_open_easy(ct, nonce,
      fromB64(theirPublicKeyB64), fromB64(myPrivateKeyB64)));
    off += len;
  }
  return parts;
}

// --- v3.0 : identité dérivée d'une passphrase (modèle portefeuille crypto) ---
// PRINCIPE : une clé privée perdue (navigateur qui purge IndexedDB) est
// irrécupérable — SAUF si la paire de clés est DÉRIVÉE d'un secret que
// l'utilisateur détient. Ici : passphrase → Argon2id → graine 32 octets →
// crypto_box_seed_keypair. Pseudo + passphrase régénèrent exactement la même
// identité sur n'importe quel appareil. Rien n'est stocké nulle part (le sel
// est déterministe, dérivé du pseudo) : 100 % local, zéro donnée serveur.
//
// SÉCURITÉ : la clé publique étant dans l'annuaire, un attaquant connaissant
// un pseudo peut tenter une force brute HORS LIGNE de la passphrase. Parade :
// passphrase GÉNÉRÉE de 9 mots (~72 bits) + Argon2id. C'est pourquoi la
// passphrase générée est le choix par défaut, un mot de passe humain choisi
// étant structurellement plus faible (toléré à partir de 12 caractères, avec
// avertissement). Paramètres Argon2id INTERACTIVE (64 Mio) : compatibles
// mobile ; largement suffisants face à 72 bits d'entropie.

export function generatePassphrase(nWords = 9) {
  const idx = sodium.randombytes_buf(nWords);
  return Array.from(idx, (b) => WORDS[b]).join(' ');
}

function saltForPseudo(pseudo) {
  // Sel déterministe : même (pseudo, passphrase) → même paire, sur tout appareil.
  return sodium.crypto_generichash(
    sodium.crypto_pwhash_SALTBYTES,
    sodium.from_string('messagerie-e2e:v3:' + pseudo.trim().toLowerCase())
  );
}

export function deriveKeyPairFromPassphrase(pseudo, passphrase) {
  const seed = sodium.crypto_pwhash(
    sodium.crypto_box_SEEDBYTES,
    passphrase.trim().replace(/\s+/g, ' '), // espaces normalisés (tolérance de saisie)
    saltForPseudo(pseudo),
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
  const kp = sodium.crypto_box_seed_keypair(seed);
  sodium.memzero(seed);
  return { publicKey: toB64(kp.publicKey), privateKey: toB64(kp.privateKey) };
}

// --- v4.0 : export/import chiffré de l'historique (Argon2id + secretbox) ---
// Même schéma que l'export d'identité : passphrase → Argon2id → secretbox.
// Le blob JSON de l'historique ne transite jamais par le serveur.
export function encryptHistory(historyObj, passphrase) {
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const key = sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES, passphrase, salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE, sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13);
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const ct = sodium.crypto_secretbox_easy(sodium.from_string(JSON.stringify(historyObj)), nonce, key);
  sodium.memzero(key);
  return JSON.stringify({ v: 1, type: 'history', salt: toB64(salt), nonce: toB64(nonce), box: toB64(ct) });
}

export function decryptHistory(fileText, passphrase) {
  const data = JSON.parse(fileText);
  if (data.type !== 'history') throw new Error('Ce fichier n’est pas un export d’historique.');
  const key = sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES, passphrase, fromB64(data.salt),
    sodium.crypto_pwhash_OPSLIMIT_MODERATE, sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13);
  let pt;
  try { pt = sodium.crypto_secretbox_open_easy(fromB64(data.box), fromB64(data.nonce), key); }
  catch (_) { throw new Error('Mot de passe incorrect ou fichier corrompu.'); }
  finally { sodium.memzero(key); }
  return JSON.parse(sodium.to_string(pt));
}



// --- v4.9.2 : sauvegarde complète chiffrée du coffre logique ---
// Contient identité + historique logique déchiffrés en mémoire puis rechiffrés
// immédiatement avec une passphrase d'export. La DEK locale et les credentials
// WebAuthn ne sont JAMAIS exportés : ils restent propres à l'appareil.
export function encryptVaultBackup(payload, passphrase) {
  const secret = String(passphrase || '');
  if (secret.length < 8 || secret.length > 256) throw new Error('Mot de passe de sauvegarde : 8 caractères minimum.');
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const key = sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES, secret, salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE, sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13);
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  try {
    const plaintext = sodium.from_string(JSON.stringify(payload));
    const ct = sodium.crypto_secretbox_easy(plaintext, nonce, key);
    return JSON.stringify({
      v: 1, type: 'vault-backup', kdf: 'argon2id13-mobile',
      opslimit: sodium.crypto_pwhash_OPSLIMIT_MODERATE,
      memlimit: sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
      salt: toB64(salt), nonce: toB64(nonce), box: toB64(ct),
    });
  } finally { sodium.memzero(key); }
}

export function decryptVaultBackup(fileText, passphrase) {
  let data;
  try { data = JSON.parse(fileText); } catch (_) { throw new Error('Fichier de sauvegarde invalide.'); }
  if (data?.v !== 1 || data?.type !== 'vault-backup' || !data.salt || !data.nonce || !data.box) {
    throw new Error('Ce fichier n’est pas une sauvegarde complète du coffre.');
  }
  const key = sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES, String(passphrase || ''), fromB64(data.salt),
    Number(data.opslimit || sodium.crypto_pwhash_OPSLIMIT_MODERATE),
    Number(data.memlimit || sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE),
    sodium.crypto_pwhash_ALG_ARGON2ID13);
  let pt;
  try { pt = sodium.crypto_secretbox_open_easy(fromB64(data.box), fromB64(data.nonce), key); }
  catch (_) { throw new Error('Mot de passe incorrect ou sauvegarde corrompue.'); }
  finally { sodium.memzero(key); }
  const payload = JSON.parse(sodium.to_string(pt));
  if (payload?.v !== 1 || payload?.type !== 'messagerie-vault-backup' || !payload?.identity || !payload?.history) {
    throw new Error('Contenu de sauvegarde incomplet ou incompatible.');
  }
  return payload;
}

// v4.0 : code de vérification numérique (façon "numéros de sécurité" Signal).
// Autre représentation du MÊME hash de clé publique : 60 chiffres groupés par 5,
// plus faciles à lire à voix haute que l'hexadécimal.
export function safetyNumber(publicKeyB64) {
  const h = sodium.crypto_generichash(30, fromB64(publicKeyB64)); // 30 octets → 60 chiffres
  let digits = '';
  for (const b of h) digits += String(b % 10) + String(Math.floor(b / 10) % 10);
  return digits.match(/.{1,5}/g).join(' ');
}
